import { useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import { DeleteForever, Restore } from '@mui/icons-material'
import { fetchDeletedData, restoreRecords, permanentDeleteRecords } from '../../api/deleted-data'
import { useApi } from '../../api/client'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { useToastStore } from '../../stores/toast.store'

export function DeletedData() {
  const toast = useToastStore()
  const { data: objectsData } = useApi<{ data: Array<{ id: string; name: string; label: string }> }>('/objects')
  const objects = objectsData?.data ?? []

  const [selectedObject, setSelectedObject] = useState('')
  const [records, setRecords] = useState<Array<{ id: string; object_id: string; data: string | Record<string, unknown>; deleted_at?: string }>>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const loadDeleted = useCallback(async (objectId?: string) => {
    setLoading(true)
    try {
      const result = await fetchDeletedData({ object_id: objectId || undefined, limit: 100 })
      setRecords(result.data ?? [])
    } catch {
      toast.error('Failed to load deleted data')
    } finally {
      setLoading(false)
    }
  }, [toast])

  const handleObjectChange = useCallback((objectId: string) => {
    setSelectedObject(objectId)
    setSelected(new Set())
    loadDeleted(objectId || undefined)
  }, [loadDeleted])

  const handleRestore = useCallback(async (recordId: string) => {
    try {
      await restoreRecords([recordId])
      toast.success('Record restored')
      loadDeleted(selectedObject || undefined)
    } catch {
      toast.error('Failed to restore record')
    }
  }, [selectedObject, loadDeleted, toast])

  const handlePermanentDelete = useCallback(async () => {
    if (selected.size === 0) return
    try {
      await permanentDeleteRecords([...selected])
      toast.success(`${selected.size} records permanently deleted`)
      setSelected(new Set())
      setConfirmOpen(false)
      loadDeleted(selectedObject || undefined)
    } catch {
      toast.error('Failed to delete records')
    }
  }, [selected, selectedObject, loadDeleted, toast])

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selected.size === records.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(records.map((r) => r.id)))
    }
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Deleted Data</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Object</InputLabel>
          <Select value={selectedObject} label="Filter by Object" onChange={(e) => handleObjectChange(e.target.value)}>
            <MenuItem value="">All Objects</MenuItem>
            {objects.map((obj) => (
              <MenuItem key={obj.id} value={obj.id}>{obj.label || obj.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {selected.size > 0 && (
          <Button variant="contained" color="error" startIcon={<DeleteForever />} onClick={() => setConfirmOpen(true)}>
            Delete Permanently ({selected.size})
          </Button>
        )}

        <Button variant="outlined" onClick={() => loadDeleted(selectedObject || undefined)} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {records.length === 0 && !loading && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>No deleted records found</Typography>
        </Paper>
      )}

      {records.length > 0 && !loading && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={selected.size === records.length && records.length > 0} onChange={toggleAll} />
                </TableCell>
                <TableCell>Object</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Deleted At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((rec) => {
                const data = typeof rec.data === 'string' ? JSON.parse(rec.data) : rec.data
                return (
                  <TableRow key={rec.id} hover selected={selected.has(rec.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.has(rec.id)} onChange={() => toggleSelect(rec.id)} />
                    </TableCell>
                    <TableCell>{rec.object_id}</TableCell>
                    <TableCell>{String(data.name ?? data.formatted_name ?? rec.id)}</TableCell>
                    <TableCell>{rec.deleted_at}</TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<Restore />} onClick={() => handleRestore(rec.id)}>
                        Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Permanently Delete"
        message={`Are you sure you want to permanently delete ${selected.size} record(s)? This action cannot be undone.`}
        confirmLabel="Delete Permanently"
        confirmColor="error"
        onConfirm={handlePermanentDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  )
}
